import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { roomsData } from '../data/roomsData';
import { Users, Bath, Maximize, Check, ChevronLeft, ArrowLeft, ChevronRight } from 'lucide-react';

const RoomDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  
  const room = roomsData.find(room => room.id === id);

  useEffect(() => {
    if (!room) {
      navigate('/rooms');
    }
  }, [room, navigate]);

  if (!room) {
    return null;
  }

  const handlePrevImage = () => {
    setActiveImageIndex((prev) => (prev === 0 ? room.images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setActiveImageIndex((prev) => (prev === room.images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div>
      {/* Room Gallery */}
      <section className="pt-24 bg-sage-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Link to="/rooms" className="flex items-center text-sage-700 hover:text-sage-800 font-medium">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Rooms
            </Link>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Image Gallery */}
            <div className="w-full lg:w-2/3">
              <div className="relative overflow-hidden rounded-lg shadow-lg h-96">
                <img 
                  src={room.images[activeImageIndex]} 
                  alt={`${room.name} view ${activeImageIndex + 1}`} 
                  className="w-full h-full object-cover"
                />
                
                <button 
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-colors"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                
                <button 
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-colors"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                  {room.images.map((_, idx) => (
                    <button 
                      key={idx}
                      onClick={() => setActiveImageIndex(idx)}
                      className={`w-2.5 h-2.5 rounded-full transition-colors ${
                        idx === activeImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-2 mt-2">
                {room.images.map((image, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`overflow-hidden rounded-md ${
                      idx === activeImageIndex ? 'ring-2 ring-sage-700' : ''
                    }`}
                  >
                    <img 
                      src={image} 
                      alt={`${room.name} thumbnail ${idx + 1}`} 
                      className="w-full h-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
            
            {/* Room Details */}
            <div className="w-full lg:w-1/3 bg-white rounded-lg p-6 shadow-md self-start">
              <h1 className="text-2xl font-serif font-bold text-gray-800 mb-2">{room.name}</h1>
              <div className="flex items-center mb-4">
                <span className="text-2xl font-bold text-sage-700">RON{room.price}</span>
                <span className="text-gray-600 ml-1">/noapte</span>
              </div>
              
              <div className="border-t border-gray-200 my-4 pt-4">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center text-gray-600">
                    <Users className="h-5 w-5 mr-2 text-sage-700" />
                    <div>
                      <p className="text-sm text-gray-500">Guests</p>
                      <p className="font-medium">{room.capacity}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Maximize className="h-5 w-5 mr-2 text-sage-700" />
                    <div>
                      <p className="text-sm text-gray-500">Size</p>
                      <p className="font-medium">{room.size} m²</p>
                    </div>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Bath className="h-5 w-5 mr-2 text-sage-700" />
                    <div>
                      <p className="text-sm text-gray-500">Bathroom</p>
                      <p className="font-medium">{room.bathroom}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Check className="h-5 w-5 mr-2 text-sage-700" />
                    <div>
                      <p className="text-sm text-gray-500">Bed Type</p>
                      <p className="font-medium">{room.bed}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <Link
                to={`/book?room=${room.id}`}
                className="w-full bg-sage-700 hover:bg-sage-800 text-white py-3 rounded-md transition-colors text-center block font-medium"
              >
                Rezerva camera
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Room Description */}
      <section className="py-12 bg-sage-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="bg-white rounded-lg p-6 shadow-md mb-8">
            <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Descriere</h2>
            <p className="text-gray-600 mb-4">{room.description}</p>
            <p className="text-gray-600">{room.longDescription}</p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-md">
            <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Facilități</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {room.amenities.map((amenity, index) => (
                <div key={index} className="flex items-center">
                  <Check className="h-5 w-5 text-sage-700 mr-2" />
                  <span className="text-gray-700">{amenity}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Other Rooms */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-6"></h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {roomsData
              .filter(r => r.id !== room.id)
              .slice(0, 3)
              .map((otherRoom) => (
                <div 
                  key={otherRoom.id} 
                  className="bg-white rounded-lg overflow-hidden shadow-md transition-all hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="relative overflow-hidden">
                    <img 
                      src={otherRoom.images[0]} 
                      alt={otherRoom.name} 
                      className="w-full h-48 object-cover" 
                    />
                    <div className="absolute top-3 right-3 bg-white px-2 py-1 rounded-full text-sage-700 font-medium text-sm shadow">
                      ${otherRoom.price}/night
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">{otherRoom.name}</h3>
                    <div className="flex gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        <span>{otherRoom.capacity}</span>
                      </div>
                      <div className="flex items-center">
                        <Maximize className="h-4 w-4 mr-1" />
                        <span>{otherRoom.size} m²</span>
                      </div>
                    </div>
                    <Link
                      to={`/rooms/${otherRoom.id}`}
                      className="text-sage-700 hover:text-sage-800 font-medium text-sm"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoomDetail;